import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Check, X, Flame, Trophy, ArrowRight, Sparkles, User as UserIcon, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const ICON_MAP = {
  Flame, Trophy, ArrowRight, Sparkles, UserIcon, Clock,
};

function timeAgo(iso, t) {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return t("notifications.just_now");
  if (sec < 3600) return t("notifications.minutes_ago", { n: Math.floor(sec / 60) });
  if (sec < 86400) return t("notifications.hours_ago", { n: Math.floor(sec / 3600) });
  return t("notifications.days_ago", { n: Math.floor(sec / 86400) });
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(id);
  }, [load]);

  const markRead = async (notif) => {
    if (!notif.read) {
      await api.post(`/notifications/${notif.id}/read`);
      setItems(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    }
    if (notif.link) {
      setOpen(false);
      navigate(notif.link);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
      toast.success(t("notifications.mark_all_read"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (e, notifId) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${notifId}`);
      setItems(prev => prev.filter(n => n.id !== notifId));
    } catch (e) { /* silent */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost" size="icon"
          className="relative h-8 w-8"
          data-testid="notification-bell"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-medium flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="end" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="text-sm font-medium">{t("notifications.title")}</div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} className="h-7 text-xs"
                    data-testid="notification-mark-all-read">
              <Check className="h-3 w-3 mr-1" /> {t("notifications.mark_all_read")}
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-8" data-testid="notification-empty">
              {t("notifications.empty")}
            </div>
          )}
          {items.map((n) => {
            const Icon = ICON_MAP[n.icon] || Sparkles;
            const sevClass = n.severity === "success" ? "text-emerald-600 bg-emerald-50"
                            : n.severity === "warning" ? "text-amber-600 bg-amber-50"
                            : "text-primary bg-primary/10";
            return (
              <div
                key={n.id}
                className={`group flex items-start gap-2.5 px-3 py-2.5 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted/40 transition-colors ${!n.read ? "bg-primary/[0.03]" : ""}`}
                onClick={() => markRead(n)}
                data-testid={`notification-item-${n.id}`}
              >
                <div className={`h-6 w-6 rounded flex items-center justify-center shrink-0 ${sevClass}`}>
                  <Icon className="h-3 w-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium truncate">{n.title}</div>
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at, t)}</div>
                </div>
                <Button
                  variant="ghost" size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  onClick={(e) => remove(e, n.id)}
                  data-testid={`notification-delete-${n.id}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
